/**
 * DummyData.js · static mock data for the UI components
 * ---------------------------------------------------------------------
 * Ready-made fixtures for  <ScreeningPanel>  and  <AuditStepper>.
 * Import what you need, e.g.
 *
 *   import ScreeningPanel from './ScreeningPanel';
 *   import { screeningHits, auditRejected } from './DummyData';
 *
 *   <ScreeningPanel {...screeningHits} />
 *   <AuditStepper steps={auditRejected.steps} />
 *
 * All dates are parsed by the components and rendered as dd/mm/yyyy hh:mm.
 * Demo entity: ACB Global Ltd · ABN 62 409 113 088.
 */

/* ================================================================== */
/*  ScreeningPanel fixtures                                            */
/* ================================================================== */
/*  props: decisions, sourcesChecked, matchedSources, latest,
 *         sanctionsResults, screeningDate                             */

const commonSources = [
  'DFAT Consolidated',
  'UN',
  'OFAC SDN',
  'EU',
  'Global PEP',
  'domestic and foreign',
  'RCA',
  'Global adverse media',
  'AU tier 1',
];

/** ⚠️ Multiple hits — sanctions (2 lists) + PEP (2 lists) both flagged. */
export const screeningHits = {
  decisions: {
    sanctions: true,
    pep: true,
    adverse_media: false,
    watchlist: false,
  },
  sourcesChecked: commonSources,
  matchedSources: ['UN', 'OFAC SDN', 'Global PEP', 'RCA'],
  latest: '2026-09-04T09:41:00', // renders 04/09/2026 09:41
  sanctionsResults: null,
  screeningDate: '-',
};

/** ✅ All clear — every register returns no match. */
export const screeningClear = {
  decisions: {
    sanctions: false,
    pep: false,
    adverse_media: false,
    watchlist: false,
  },
  sourcesChecked: commonSources,
  matchedSources: [],
  latest: '2026-09-07T14:05:00',
  sanctionsResults: null,
  screeningDate: '-',
};

/** 👤 Single PEP match only — exercises the "1 MATCH" result pill. */
export const screeningPepOnly = {
  decisions: {
    sanctions: false,
    pep: true,
    adverse_media: false,
    watchlist: false,
  },
  sourcesChecked: commonSources,
  matchedSources: ['Global PEP', 'RCA'],
  latest: '2026-09-06T10:22:00',
  sanctionsResults: null,
  screeningDate: '-',
};

/** 🌐 Single sanctions hit with NO matchedSources → falls back to sourcesChecked. */
export const screeningNoSources = {
  decisions: {
    sanctions: true,
    pep: false,
    adverse_media: false,
    watchlist: false,
  },
  sourcesChecked: ['DFAT Consolidated', 'UN', 'OFAC SDN', 'EU'],
  matchedSources: [],
  latest: '2026-09-05T16:50:00',
  sanctionsResults: null,
  screeningDate: '-',
};

/** Empty / fresh state — nothing screened yet. */
export const screeningEmpty = {
  decisions: {},
  sourcesChecked: [],
  matchedSources: [],
  latest: null,
  sanctionsResults: null,
  screeningDate: '-',
};

/* ================================================================== */
/*  AuditStepper fixtures                                              */
/* ================================================================== */
/*  step: { id, label, status, completedAt | date | timestamp |
 *          updatedAt, note? }                                        */

/** ✅ Happy path — 3 completed, 1 in progress, 1 pending. */
export const auditHappy = {
  steps: [
    {
      id: 'submitted',
      label: 'Application submitted',
      status: 'completed',
      completedAt: '2026-09-01T10:12:00',
      note: 'Received with all required attachments. No corrections flagged.',
    },
    {
      id: 'verify',
      label: 'Document verification',
      status: 'completed',
      completedAt: '2026-09-02T14:38:00',
      note: 'Identity and financial documents confirmed by the verification engine.',
    },
    {
      id: 'screen',
      label: 'Compliance screening',
      status: 'completed',
      completedAt: '2026-09-04T09:41:00',
      note: 'Sanctions, PEP and adverse-media registers were checked — all clear.',
    },
    {
      id: 'risk',
      label: 'Risk assessment',
      status: 'in_progress',
      note: 'Underwriting model is scoring the case. Estimated time to finish: ~40 seconds.',
    },
    {
      id: 'approval',
      label: 'Final approval',
      status: 'pending',
      note: 'Queued behind the risk assessment. An approver is notified automatically.',
    },
  ],
};

/** ⚠️ Flagged — a step was sent back for resubmission (warning). */
export const auditFlagged = {
  steps: [
    {
      id: 'submitted',
      label: 'Application submitted',
      status: 'completed',
      completedAt: '2026-08-28T09:05:00',
      note: 'Received with all required attachments.',
    },
    {
      id: 'verify',
      label: 'Document verification',
      status: 'completed',
      completedAt: '2026-08-29T16:20:00',
      note: 'ID documents matched the registry in Australia and abroad.',
    },
    {
      id: 'screen',
      label: 'Compliance screening',
      status: 'warning',
      updatedAt: '2026-09-01T11:47:00',
      note: 'A name variant appeared in adverse media. Corrected information was requested.',
    },
    {
      id: 'risk',
      label: 'Risk assessment',
      status: 'in_progress',
      note: 'Re-scoring after the compliance resubmission.',
    },
    {
      id: 'approval',
      label: 'Final approval',
      status: 'pending',
      note: 'Starts once risk assessment completes.',
    },
  ],
};

/** ❌ Rejected — shows the red node, X glyph and full red connector. */
export const auditRejected = {
  steps: [
    {
      id: 'submitted',
      label: 'Application submitted',
      status: 'completed',
      completedAt: '2026-08-25T09:05:00',
      note: 'Received with all required attachments.',
    },
    {
      id: 'verify',
      label: 'Document verification',
      status: 'rejected',
      updatedAt: '2026-08-27T13:40:00',
      note: 'The uploaded ID could not be matched to the applicant. The application is blocked until a valid document is provided.',
    },
    {
      id: 'correct',
      label: 'Corrected submission',
      status: 'in_progress',
      note: 'Applicant was prompted to upload a valid document — waiting for the new file.',
    },
    {
      id: 'rescreen',
      label: 'Re-screening',
      status: 'pending',
      note: 'Runs automatically once the corrected document is uploaded.',
    },
    {
      id: 'approval',
      label: 'Final approval',
      status: 'pending',
      note: 'Starts after re-screening passes.',
    },
  ],
};

/** 2-step minimal — shortest layout for narrow cards. */
export const auditMinimal = {
  steps: [
    {
      id: 'start',
      label: 'Application started',
      status: 'completed',
      completedAt: '2026-09-08T08:15:00',
      note: 'Journey started.',
    },
    {
      id: 'next',
      label: 'In review',
      status: 'in_progress',
      note: 'Under review now.',
    },
  ],
};

/* ================================================================== */
/*  Convenience: all fixtures keyed by name (for galleries / dev)      */
/* ================================================================== */

export default {
  screeningHits,
  screeningClear,
  screeningPepOnly,
  screeningNoSources,
  screeningEmpty,
  auditHappy,
  auditFlagged,
  auditRejected,
  auditMinimal,
};
